import { RangeSelection } from '../types';

const LINE_NUM_REGEX = /-(\d+(?::\d+)?(?:,\d+(?::\d+)?)*)$/;

export const buildLineUrlFragment = (lineNum: number) => `lines-${lineNum}`;

export const buildRangeUrlFragment = (ranges: RangeSelection[]) => {
  if (ranges.length === 0) {
    return `lines-`;
  }

  const rangeSegment = ranges.map(r =>
    r.end ? `${r.start}:${r.end}` : r.start
  );
  const dedupedHash = Array.from(new Set(rangeSegment)).join(',');
  return `lines-${dedupedHash}`;
};

export const updateActiveRanges = (
  ranges: RangeSelection[],
  lineNum: number
) => {
  let rangesUpdated = false;

  // filter duplicate ranges
  const filteredRanges = ranges.filter(
    r => !(r.start === lineNum && (r.end === lineNum || !r.end))
  );
  rangesUpdated = ranges.length !== filteredRanges.length;

  // create new ranges if lineNum is inside existing ranges
  let sortedRanges: RangeSelection[] = [];
  filteredRanges.forEach(r => {
    if (!r.end) {
      sortedRanges = [...sortedRanges, r];
    } else if (lineNum > r.start && lineNum < r.end) {
      rangesUpdated = true;
      sortedRanges = [
        ...sortedRanges,
        { start: r.start, end: lineNum - 1 },
        { start: lineNum + 1, end: r.end },
      ];
    } else if (lineNum === r.start) {
      rangesUpdated = true;
      sortedRanges = [...sortedRanges, { start: lineNum + 1, end: r.end }];
    } else if (lineNum === r.end) {
      rangesUpdated = true;
      sortedRanges = [...sortedRanges, { start: r.start, end: r.end - 1 }];
    } else {
      sortedRanges = [...sortedRanges, r];
    }
  });

  const updatedRanges = rangesUpdated
    ? sortedRanges.map(r => (r.start === r.end ? { start: r.start } : r))
    : [...ranges, { start: lineNum }];

  return buildRangeUrlFragment(updatedRanges);
};

export const extendRangeUrlFragment = (
  ranges: RangeSelection[],
  newLineNum: number
): string | null | undefined => {
  if (ranges.length === 0) {
    return null;
  }

  const selectedRanges = [...ranges];
  const prevClickedRange = {
    ...selectedRanges[selectedRanges.length - 1],
  };

  const { start } = prevClickedRange;
  const end = prevClickedRange.end ? prevClickedRange.end : start;

  if (newLineNum < start) {
    selectedRanges.pop();
    const newRanges = [...selectedRanges, { start: newLineNum, end }];
    return buildRangeUrlFragment(newRanges);
  }

  if (newLineNum > start) {
    selectedRanges.pop();
    const newRanges = [...selectedRanges, { start, end: newLineNum }];
    return buildRangeUrlFragment(newRanges);
  }

  return null;
};

export const parseLineNums = (urlFragment: string): RangeSelection[] => {
  const match = urlFragment.match(LINE_NUM_REGEX);
  return match
    ? match[1].split(',').map(range => {
        const [start, end] = range.split(':').map(Number);
        return { start, end };
      })
    : [];
};
